<template>
  <header :class="{ 'is-scrolled': isScrolled || isMenuOpen }" class="header">
    <div class="container">
      <router-link to="/" class="brand-wrapper" @click="isMenuOpen = false">
        <img
          src="@/assets/images/LOGONO.png"
          alt="Clients Preferred Property Hub"
          class="brand-logo"
        />
        <div class="brand-text">
          <div class="brand-main">
            <span class="ruby">CLIENTS</span>
            <span class="thambiah"> PREFERRED</span>
          </div>
          <span class="brand-sub">Property Hub</span>
        </div>
      </router-link>

      <button
        class="burger-btn"
        :class="{ 'is-active': isMenuOpen }"
        @click="isMenuOpen = !isMenuOpen"
        aria-label="Toggle Menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <nav class="nav-menu" :class="{ 'is-open': isMenuOpen }">
        <router-link to="/" class="nav-link" @click="isMenuOpen = false"
          >Home</router-link
        >
        <router-link
          to="/associatedbuilders"
          class="nav-link"
          @click="isMenuOpen = false"
          >Builders</router-link
        >
        <router-link to="/buyer" class="nav-link" @click="isMenuOpen = false"
          >Buyer</router-link
        >
        <router-link to="/about" class="nav-link" @click="isMenuOpen = false"
          >About</router-link
        >
        <router-link
          to="/contactus"
          class="contact-btn"
          @click="isMenuOpen = false"
          >Contact Us</router-link
        >
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const isScrolled = ref(false);
const isMenuOpen = ref(false);

// Safety: Update scroll only if component is mounted
const handleScroll = () => {
  if (typeof window !== 'undefined') {
    isScrolled.value = window.scrollY > 50;
  }
};

// Safety: Close mobile menu if window is resized to desktop width
const handleResize = () => {
  if (window.innerWidth > 992 && isMenuOpen.value) {
    isMenuOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background: transparent;
}

.is-scrolled {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  padding: 10px 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

/* --- Brand/Logo Styling --- */
.brand-wrapper {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1001;
}

.brand-logo {
  height: 50px;
  width: auto;
  border-radius: 4px;
  transition: height 0.3s ease;
}

.is-scrolled .brand-logo {
  height: 42px;
}

.brand-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.brand-main {
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  line-height: 1;
}

.ruby {
  color: #b91c1c;
}

.thambiah {
  color: #333;
}

.brand-sub {
  font-size: 0.7rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-top: 3px;
}

/* --- Desktop Menu --- */
.nav-menu {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-link {
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 0.95rem;
  position: relative;
  transition: color 0.3s;
}

/* Active Link Styling */
.router-link-active.nav-link {
  color: #b91c1c;
}

.router-link-active.nav-link::after {
  width: 100%;
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -5px;
  left: 0;
  background-color: #b91c1c;
  transition: width 0.3s;
}

.nav-link:hover::after {
  width: 100%;
}
.nav-link:hover {
  color: #b91c1c;
}

/* --- Contact Button Styling --- */
.contact-btn {
  background: #b91c1c;
  color: white !important;
  padding: 12px 28px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  border: 2px solid #b91c1c;
  display: inline-block;
}

.contact-btn:hover {
  background: transparent;
  color: #b91c1c !important;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(185, 28, 28, 0.2);
}

/* Hamburger Icon */
.burger-btn {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 21px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1001;
  padding: 0;
}

.burger-btn span {
  width: 100%;
  height: 3px;
  background-color: #333;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.burger-btn.is-active span:nth-child(1) {
  transform: translateY(9px) rotate(45deg);
}
.burger-btn.is-active span:nth-child(2) {
  opacity: 0;
}
.burger-btn.is-active span:nth-child(3) {
  transform: translateY(-9px) rotate(-45deg);
}

/* Responsive Styles */
@media (max-width: 992px) {
  .burger-btn {
    display: flex;
  }

  .nav-menu {
    position: fixed;
    top: 0;
    right: -100%;
    width: 85%;
    height: 100vh;
    background: white;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 40px;
    transition: right 0.4s ease;
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
  }

  .nav-menu.is-open {
    right: 0;
  }

  .nav-link {
    font-size: 1.5rem;
    font-weight: 700;
  }

  .contact-btn {
    font-size: 1.2rem;
    padding: 15px 40px;
    width: auto;
  }
}
</style>
